import { useEffect, useState } from 'react';
import { chatSend } from '../../../assets/images/images';
import { formatMessage, parseBulletPoints } from '../../../utility/helpers';
import {
    getSectionNameToKey,
    getSectionOptions,
    PRIORITY_LABELS,
    PRIORITY_KEYS,
    RESPONSIBILITIES_KEYS,
    RESPONSIBILITIES_LABELS,
    AUTO_POPULATE_FIELDS
} from '../../../utility/chatSections';
import { useSelectionState } from '../../../hooks/useSelectionState';
import { useSuggestionState } from '../../../hooks/useSuggestionState';
import OptionButton from './shared/OptionButton';
import MessageContainer from './shared/MessageContainer';
import { IncomingMessageJobProps } from './shared/types';

/**
 * IncomingMessageJob - For ChatWithJob (Job Description Editing)
 * Features:
 * - Prioritize responsibilities and key sections
 * - "More/Less" toggle for less important options
 * - Sort by length
 * - Complex suggestion routing
 */
const IncomingMessageJob = ({
    chat,
    message,
    isLoading,
    isFromJob = false,
    setLastMessageId,
    handleSendMessage,
    setEnableGenerateResponse,
    setLastQuestionStep,
    onAddFieldSuggestion,
    onSelectSuggestion,
    onMoveSuggestions,
    isLastMessage,
}: IncomingMessageJobProps) => {
    const isMultiSelect = chat?.is_multi_select;
    const [showAllOptions, setShowAllOptions] = useState<boolean>(false);

    const {
        selectedOptions,
        optionKeys,
        renderKey,
        handleOptionClick,
        setSelectionsFromJSON
    } = useSelectionState(isMultiSelect);

    const {
        selectedSuggestions,
        pendingSuggestions,
        renderKey: suggestionRenderKey,
        handleSuggestionClick,
        setPendingSuggestions,
        setSelectedSuggestions
    } = useSuggestionState();

    useEffect(() => {
        setSelectionsFromJSON(chat?.select_json);
    }, [chat?.select_json, chat?.id, setSelectionsFromJSON]);

    useEffect(() => {
        if (chat.id && !chat?.is_local && typeof chat.id === 'number') {
            setLastMessageId(chat.id);
        }
        if (chat?.step && typeof setLastQuestionStep === 'function') {
            setLastQuestionStep(chat?.step);
        }
        if (chat?.is_finished) {
            setEnableGenerateResponse(chat?.is_finished);
        }
    }, [chat, setLastQuestionStep, setLastMessageId, setEnableGenerateResponse]);

    const handleKeyActivate = (e: React.KeyboardEvent, onActivate: () => void) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onActivate();
        }
    };

    const handleMultiClick = () => {
        if (selectedOptions.length === 0) return;

        if (!isMultiSelect && selectedOptions.length === 1) {
            const option = selectedOptions[0];
            const key = optionKeys.get(option);

            if (key) {
                handleSendMessage({ [key]: option }, option);
            } else {
                handleSendMessage([option], option);
            }
        } else {
            handleSendMessage(selectedOptions, '');
        }
    };

    const renderMoreToggle = (remainingCount: number, visibleCount: number) => {
        if (remainingCount === 0) return null;
        if (!showAllOptions && visibleCount <= 1) return null;

        return (
            <div
                className={`chat-option`}
                title={showAllOptions ? 'Show less' : 'Show more'}
                onClick={() => setShowAllOptions(!showAllOptions)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => handleKeyActivate(e, () => setShowAllOptions(!showAllOptions))}
            >
                <p className="chat-option-des">{showAllOptions ? 'Less' : 'More'}</p>
            </div>
        );
    };

    const renderOptions = () => {
        // Disable options ONLY if it's not the last message.
        const isDisabled = !isLastMessage;

        if (message?.title && !Array.isArray(message?.title) && typeof message?.title === 'object' && message.title !== null) {
            // Object-based titles - prioritize and sort by length
            const entries = Object.entries(message.title)
                .filter(([key, value]) => key !== 'could_have')
                .filter(([key, value]) => !(value as string).toLowerCase().includes('what makes you a great candidate'));

            const responsibilities = entries.filter(([key, value]) =>
                RESPONSIBILITIES_KEYS.includes(key) || RESPONSIBILITIES_LABELS.includes(String(value))
            );
            const otherPrioritized = entries.filter(([key, value]) =>
                (PRIORITY_KEYS.includes(key) || PRIORITY_LABELS.includes(String(value))) &&
                !(RESPONSIBILITIES_KEYS.includes(key) || RESPONSIBILITIES_LABELS.includes(String(value)))
            );
            const remaining = entries.filter(([key, value]) =>
                !(PRIORITY_KEYS.includes(key) || PRIORITY_LABELS.includes(String(value)))
            );

            const sortByLength = (a: [string, unknown], b: [string, unknown]) => {
                return String(b[1]).length - String(a[1]).length;
            };
            const sortedResponsibilities = responsibilities.sort(sortByLength);
            const sortedOtherPrioritized = otherPrioritized.sort(sortByLength);
            const sortedRemaining = remaining.sort(sortByLength);

            const prioritized = [...sortedResponsibilities, ...sortedOtherPrioritized];

            let visibleEntries: [string, unknown][];
            let shouldShowMoreToggle = true;
            if (prioritized.length === 0) {
                visibleEntries = sortedRemaining;
                shouldShowMoreToggle = false;
            } else if (showAllOptions) {
                visibleEntries = [...prioritized, ...sortedRemaining];
                shouldShowMoreToggle = sortedRemaining.length > 0;
            } else {
                visibleEntries = prioritized;
                shouldShowMoreToggle = sortedRemaining.length > 0;
            }

            return (
                <>
                    {visibleEntries.map(([key, value], index: number) => {
                        const isSelected = selectedOptions.includes(value as string);
                        return (
                            <OptionButton
                                key={`${key}-${index}`}
                                value={value as string}
                                keyProp={key}
                                index={index}
                                isSelected={isSelected}
                                renderKey={renderKey}
                                isDisabled={isDisabled}
                                onClick={handleOptionClick}
                            />
                        );
                    })}
                    {shouldShowMoreToggle && renderMoreToggle(sortedRemaining.length, visibleEntries.length)}
                </>
            );
        } else if (message?.title && Array.isArray(message?.title)) {
            // Array-based titles - prioritize and sort by length
            const options = message.title
                .filter((option: string) => option !== 'Could-Haves')
                .filter((option: string) => !option.toLowerCase().includes('what makes you a great candidate'));

            const responsibilities = options.filter((option: string) => RESPONSIBILITIES_LABELS.includes(option));
            const otherPrioritized = options.filter((option: string) =>
                PRIORITY_LABELS.includes(option) && !RESPONSIBILITIES_LABELS.includes(option)
            );
            const remaining = options.filter((option: string) => !PRIORITY_LABELS.includes(option));

            const sortByLength = (a: string, b: string) => b.length - a.length;
            const sortedResponsibilities = responsibilities.sort(sortByLength);
            const sortedOtherPrioritized = otherPrioritized.sort(sortByLength);
            const sortedRemaining = remaining.sort(sortByLength);

            const prioritized = [...sortedResponsibilities, ...sortedOtherPrioritized];

            let visible: string[];
            let shouldShowMoreToggle = true;
            if (prioritized.length === 0) {
                visible = sortedRemaining;
                shouldShowMoreToggle = false;
            } else if (showAllOptions) {
                visible = [...prioritized, ...sortedRemaining];
                shouldShowMoreToggle = sortedRemaining.length > 0;
            } else {
                visible = prioritized;
                shouldShowMoreToggle = sortedRemaining.length > 0;
            }

            return (
                <>
                    {visible.map((option: string, index: number) => {
                        const isSelected = selectedOptions.includes(option);
                        return (
                            <OptionButton
                                key={`${option}-${index}`}
                                value={option}
                                keyProp={null}
                                index={index}
                                isSelected={isSelected}
                                renderKey={renderKey}
                                isDisabled={isDisabled}
                                onClick={handleOptionClick}
                            />
                        );
                    })}
                    {shouldShowMoreToggle && renderMoreToggle(sortedRemaining.length, visible.length)}
                </>
            );
        }
        return null;
    };

    const inferTargetSection = () => {
        if (typeof message?.section === 'string') return message.section;
        if (typeof message?.title === 'string' && !Array.isArray(message.title) && typeof message.title !== 'object') return message.title;
        if (chat?.select_json && typeof chat.select_json === 'object' && !Array.isArray(chat.select_json) && Object.keys(chat.select_json).length > 0) {
            const firstKey = Object.keys(chat.select_json)[0];
            return chat.select_json[firstKey] || firstKey;
        }
        if (chat?.payload?.title && typeof chat.payload.title === 'object' && !Array.isArray(chat.payload.title) && Object.keys(chat.payload.title).length > 0) {
            const firstKey = Object.keys(chat.payload.title)[0];
            return chat.payload.title[firstKey] || firstKey;
        }
        return "What are the day-to-day responsibilities of the role?";
    };

    const handleSendSuggestions = async () => {
        const sectionNameToKey = getSectionNameToKey('job');
        const hasExplicitSection = (typeof message?.section === 'string') || (typeof message?.title === 'string' && typeof message?.title !== 'object' && !Array.isArray(message?.title));
        const hasSelectJsonWithSection = chat?.select_json && typeof chat.select_json === 'object' && !Array.isArray(chat.select_json) && Object.keys(chat.select_json).length > 0;
        const hasPayloadSection = chat?.payload?.title && typeof chat.payload.title === 'object' && !Array.isArray(chat.payload.title) && Object.keys(chat.payload.title).length > 0;
        const hasTargetSection = hasExplicitSection || hasSelectJsonWithSection || hasPayloadSection;
        const isFollowUpSuggestion = !hasTargetSection && Array.isArray(message?.message) && message.message.length > 0;
        const shouldAutoTarget = onAddFieldSuggestion && (hasTargetSection || chat?.select_json || message?.title || isFollowUpSuggestion);

        if (shouldAutoTarget && onAddFieldSuggestion) {
            const actualSection = inferTargetSection();

            // If section is "General Suggestions", do not auto-populate.
            // Instead, fall through to setPendingSuggestions so user can choose.
            if (actualSection?.toLowerCase() !== 'general suggestions') {
                if (AUTO_POPULATE_FIELDS.includes(actualSection)) {
                    onAddFieldSuggestion(actualSection, selectedSuggestions);
                } else {
                    const fieldName = sectionNameToKey[actualSection] || actualSection;
                    onAddFieldSuggestion(fieldName, selectedSuggestions);
                }
                setSelectedSuggestions([]);
                return;
            }
        }

        setPendingSuggestions(selectedSuggestions);
    };

    const renderSectionSelector = (suggestion: string | string[]) => {
        const sectionOptions = getSectionOptions('job');
        const sectionNameToKey = getSectionNameToKey('job');

        const handleSectionSelect = async (key: string, label: string) => {
            if (onAddFieldSuggestion) {
                if (AUTO_POPULATE_FIELDS.includes(label)) {
                    onAddFieldSuggestion(label, Array.isArray(suggestion) ? suggestion : [suggestion]);
                } else {
                    const fieldName = sectionNameToKey[label] || label;
                    onAddFieldSuggestion(fieldName, Array.isArray(suggestion) ? suggestion : [suggestion]);
                }
            }
            setPendingSuggestions(null);
            setSelectedSuggestions([]);
        };

        return (
            <>
                <p className="icp-action-hint">
                    Choose which section you want me to add {Array.isArray(suggestion) && suggestion.length === 1 ? 'this' : 'these'}
                </p>
                <div className="chat-single-option no-image-option">
                    {sectionOptions.map(({ key, label }) => (
                        <div
                            key={key}
                            className={`chat-option`}
                            title={`Add to ${label}`}
                            onClick={() => handleSectionSelect(key, label)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => handleKeyActivate(e, () => handleSectionSelect(key, label))}
                        >
                            <p className="chat-option-des">{label}</p>
                        </div>
                    ))}
                    <div
                        className={`chat-option chat-option-cancel`}
                        title={`Cancel`}
                        onClick={() => setPendingSuggestions(null)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => handleKeyActivate(e, () => setPendingSuggestions(null))}
                    >
                        <p className="chat-option-des">Cancel</p>
                    </div>
                </div>
            </>
        );
    };

    const generateDetailString = (details: Record<string, string | string[]>) => {
        return Object.entries(details).map(([key, value]) => {
            const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
            const formattedValue = Array.isArray(value) ? value.join(', ') : value;

            return (
                <div key={key} className="final-received">
                    <span className="inline-medium-text">{formattedKey}:</span> {formattedValue}
                </div>
            );
        });
    };

    const renderMessageContent = () => {
        if (!message) {
            return <p><span className="inline-bold-text">Hunter: </span></p>;
        }

        if (message?.is_initial) {
            const messageText = message?.message || "Welcome! Let's edit your job description.";
            return <p><span className="inline-bold-text">Hunter: </span>{messageText}</p>;
        }

        if (message?.out_of_context === true) {
            const outOfContextMessage = Array.isArray(message?.message)
                ? message.message[0]
                : message?.message || "Sorry, that's out of my context.";

            return (
                <p>
                    <span className="inline-bold-text">Hunter: </span>
                    {outOfContextMessage}
                </p>
            );
        }

        if (message?.general_info === true) {
            const generalInfoMessage = Array.isArray(message?.message)
                ? message.message[0]
                : message?.message || "Here's the information you requested.";

            return (
                <p>
                    <span className="inline-bold-text">Hunter: </span>
                    {generalInfoMessage}
                </p>
            );
        }

        if (message?.title && (Array.isArray(message?.title) || (typeof message?.title === 'object' && message.title !== null))) {
            const messageText = message?.message ? formatMessage(message.message) : "Please choose from the following options:";
            return (
                <p>
                    <span className="inline-bold-text">Hunter: </span>
                    {messageText}
                </p>
            );
        }

        // Handle suggestions
        if (message?.message && Array.isArray(message?.message)) {
            const hasTargetSection = (typeof message?.section === 'string') ||
                (typeof message?.title === 'string' && typeof message?.title !== 'object' && !Array.isArray(message?.title));
            // Disable suggestions if this message has already been responded to or it's not the last message
            const isSuggestionsDisabled = !isLastMessage;

            return (
                <div>
                    <p>
                        <span className="inline-bold-text">Hunter: </span>
                        {hasTargetSection ? (
                            <>
                                Here are {message.message.length} {message.section} suggestions:
                                <br />
                            </>
                        ) : (
                            <>Here are some suggestions:</>
                        )}
                    </p>
                    <div className="chat-single-option no-image-option" key={`suggestions-${suggestionRenderKey}`}>
                        {message.message
                            .filter((text: any) => !text.toLowerCase().includes('what makes you a great candidate'))
                            .map((text: any, index: any) => {
                                const isSelected = selectedSuggestions.includes(text);
                                return (
                                    <div
                                        key={`${text}-${index}-${suggestionRenderKey}`}
                                        className={`chat-option ${isSelected ? 'selected' : ''} ${isSuggestionsDisabled ? 'disabled' : ''}`}
                                        title={isSuggestionsDisabled ? 'This suggestion has already been selected' : 'Click to select'}
                                        onClick={isSuggestionsDisabled ? undefined : () => handleSuggestionClick(text)}
                                        role="button"
                                        tabIndex={isSuggestionsDisabled ? -1 : 0}
                                        onKeyDown={isSuggestionsDisabled ? undefined : (e) => handleKeyActivate(e, () => handleSuggestionClick(text))}
                                        aria-disabled={isSuggestionsDisabled}
                                    >
                                        <p className="chat-option-des">{text}</p>
                                    </div>
                                );
                            })}
                    </div>
                    {selectedSuggestions.length > 0 && !isSuggestionsDisabled && (
                        <div className="chat-single-option no-image-option">
                            <img
                                src={chatSend}
                                alt="send"
                                className={`chat-send-btn`}
                                onClick={handleSendSuggestions}
                                role="button"
                                aria-label="Send selected suggestions"
                                tabIndex={0}
                                onKeyDown={(e) => handleKeyActivate(e, handleSendSuggestions)}
                            />
                        </div>
                    )}
                    {pendingSuggestions && renderSectionSelector(pendingSuggestions)}
                </div>
            );
        }

        if (message?.step === 'finish') {
            return (
                <p>
                    <span className="inline-bold-text">Hunter:</span>
                    Finished! These are details based on your provided job info:-
                    <br /><br />
                    {message?.summary && generateDetailString(message?.summary)}
                    <br />
                    Let me know if you want to change or update anything?
                </p>
            );
        }

        const messageText =
            message?.step === 'distance'
                ? `${formatMessage(message?.message)} Distances are calculated using your office location to ensure precision.`
                : formatMessage(message?.message);

        const bulletData = messageText ? parseBulletPoints(messageText) : null;

        if (bulletData?.bulletItems.length) {
            return (
                <p>
                    <span className="inline-bold-text">Hunter: </span>
                    {bulletData.introText && (
                        <>
                            <span>{bulletData.introText}</span>
                            <br />
                        </>
                    )}
                    <ul className="message-list">
                        {bulletData.bulletItems.map((item: string, index: number) => (
                            <li key={index}>{item}</li>
                        ))}
                    </ul>
                    {bulletData.closingText && (
                        <span>{bulletData.closingText}</span>
                    )}
                </p>
            );
        }

        return <p><span className="inline-bold-text">Hunter: </span>{messageText}</p>;
    };

    return (
        <>
            <MessageContainer isFromJob={isFromJob}>
                {renderMessageContent()}
            </MessageContainer>

            {((Array.isArray(message?.title) && message.title.length > 0) ||
                (!Array.isArray(message?.title) && message?.title && (typeof message?.title === 'object' && message.title !== null))) && (
                    <div className={`chat-single-option no-image-option ${isLoading ? 'p-none' : ''}`}>
                        {renderOptions()}
                        {(!chat?.select_json || isLastMessage) && (
                            <img
                                src={chatSend}
                                alt="send"
                                className={`chat-send-btn ${selectedOptions?.length > 0 ? '' : 'p-none'}`}
                                onClick={handleMultiClick}
                                role="button"
                                aria-label="Send selected options"
                                tabIndex={0}
                                onKeyDown={(e) => handleKeyActivate(e, handleMultiClick)}
                            />
                        )}
                    </div>
                )}
        </>
    );
};

export default IncomingMessageJob;

